export type PersonaVariant = 'problem_solution' | 'identity_based' | 'future_state' | 'efficiency_focus';

export interface PersonaCopy {
  hero: {
    headline: string;
    subheadline: string;
    cta: string;
  };
  form: {
    emailLabel: string;
    emailPlaceholder: string;
    emailHelper: string;
    keepUpdatedLabel: string;
    keepUpdatedHelper: string;
    submitButton: string;
    submitButtonLoading: string;
  };
  questions: {
    title: string;
    subtitle: string;
    whatWouldYouQuellect: {
      label: string;
      helper: string;
      placeholder: string;
    };
    nextPlatforms: {
      label: string;
      helper: string;
      options: string[];
    };
    primaryUseCase: {
      label: string;
      helper: string;
      options: string[];
    };
    role: {
      label: string;
      helper: string;
      options: string[];
    };
    watchLaterSize: {
      label: string;
      helper: string;
      options: string[];
    };
  };
  microcopy: {
    progressIndicator: string;
    exitIntent: {
      title: string;
      message: string;
      cta: string;
    };
    success: {
      title: string;
      message: string;
      cta: string;
    };
  };
}

export const personaCopy: Record<string, PersonaCopy> = {
  problem_solution: {
    hero: {
      headline: "Turn Saved Content Into Searchable Knowledge",
      subheadline: "Quellect uses AI to transform your content collections into a searchable knowledge base. Never lose track of important insights again.",
      cta: "Join Waitlist"
    },
    form: {
      emailLabel: "Email Address",
      emailPlaceholder: "your.email@example.com",
      emailHelper: "We'll notify you when Quellect launches.",
      keepUpdatedLabel: "Keep me updated about Quellect",
      keepUpdatedHelper: "Get product updates and early access.",
      submitButton: "Join Waitlist",
      submitButtonLoading: "Joining..."
    },
    questions: {
      title: "Help us personalize your experience",
      subtitle: "A few quick questions to make Quellect perfect for you",
      whatWouldYouQuellect: {
        label: "What would you like to Quellect?",
        helper: "Tell us about the content you want to make searchable",
        placeholder: "e.g., Machine learning tutorials, cooking videos, business podcasts..."
      },
      nextPlatforms: {
        label: "Which platforms would you like us to support next?",
        helper: "Help us prioritize which platforms to add next",
        options: ["Instagram", "TikTok", "Podcasts", "Blogs", "Other"]
      },
      primaryUseCase: {
        label: "What's your primary use case?",
        helper: "This helps us tailor the experience for you",
        options: ["Work research", "School study", "Content creation", "Personal learning", "Other"]
      },
      role: {
        label: "What's your role?",
        helper: "Understanding your role helps us customize features",
        options: ["Student", "Engineer", "Founder", "Researcher", "PM/Marketing", "Other"]
      },
      watchLaterSize: {
        label: "How big is your 'Watch Later' playlist?",
        helper: "This helps us understand your content consumption",
        options: ["< 20 videos", "20-100 videos", "100-300 videos", "300+ videos"]
      }
    },
    microcopy: {
      progressIndicator: "Step {current} of {total}",
      exitIntent: {
        title: "Wait! Don't lose your spot",
        message: "Join the Quellect waitlist and get early access when we launch.",
        cta: "Join Waitlist Now"
      },
      success: {
        title: "You're on the list! 🎉",
        message: "Thanks for joining the Quellect waitlist. We'll notify you as soon as we launch.",
        cta: "Share with friends"
      }
    }
  },
  identity_based: {
    hero: {
      headline: "For Serious Learners Who Value Knowledge",
      subheadline: "Quellect transforms how you consume and retain information from videos. Build your personal knowledge library.",
      cta: "Join Waitlist"
    },
    form: {
      emailLabel: "Email Address",
      emailPlaceholder: "your.email@example.com",
      emailHelper: "We'll notify you when Quellect launches.",
      keepUpdatedLabel: "Keep me updated about Quellect",
      keepUpdatedHelper: "Get product updates and early access.",
      submitButton: "Join Waitlist",
      submitButtonLoading: "Joining..."
    },
    questions: {
      title: "Help us personalize your experience",
      subtitle: "A few quick questions to make Quellect perfect for you",
      whatWouldYouQuellect: {
        label: "What would you like to Quellect?",
        helper: "Tell us about the content you want to make searchable",
        placeholder: "e.g., Machine learning tutorials, cooking videos, business podcasts..."
      },
      nextPlatforms: {
        label: "Which platforms would you like us to support next?",
        helper: "Help us prioritize which platforms to add next",
        options: ["Instagram", "TikTok", "Podcasts", "Blogs", "Other"]
      },
      primaryUseCase: {
        label: "What's your primary use case?",
        helper: "This helps us tailor the experience for you",
        options: ["Work research", "School study", "Content creation", "Personal learning", "Other"]
      },
      role: {
        label: "What's your role?",
        helper: "Understanding your role helps us customize features",
        options: ["Student", "Engineer", "Founder", "Researcher", "PM/Marketing", "Other"]
      },
      watchLaterSize: {
        label: "How big is your 'Watch Later' playlist?",
        helper: "This helps us understand your content consumption",
        options: ["< 20 videos", "20-100 videos", "100-300 videos", "300+ videos"]
      }
    },
    microcopy: {
      progressIndicator: "Step {current} of {total}",
      exitIntent: {
        title: "Wait! Don't lose your spot",
        message: "Join the Quellect waitlist and get early access when we launch.",
        cta: "Join Waitlist Now"
      },
      success: {
        title: "You're on the list! 🎉",
        message: "Thanks for joining the Quellect waitlist. We'll notify you as soon as we launch.",
        cta: "Share with friends"
      }
    }
  },
  future_state: {
    hero: {
      headline: "Imagine Never Losing a Video Insight Again",
      subheadline: "Quellect is the future of video learning. Your personal AI assistant that remembers everything you've watched.",
      cta: "Join Waitlist"
    },
    form: {
      emailLabel: "Email Address",
      emailPlaceholder: "your.email@example.com",
      emailHelper: "We'll notify you when Quellect launches.",
      keepUpdatedLabel: "Keep me updated about Quellect",
      keepUpdatedHelper: "Get product updates and early access.",
      submitButton: "Join Waitlist",
      submitButtonLoading: "Joining..."
    },
    questions: {
      title: "Help us personalize your experience",
      subtitle: "A few quick questions to make Quellect perfect for you",
      whatWouldYouQuellect: {
        label: "What would you like to Quellect?",
        helper: "Tell us about the content you want to make searchable",
        placeholder: "e.g., Machine learning tutorials, cooking videos, business podcasts..."
      },
      nextPlatforms: {
        label: "Which platforms would you like us to support next?",
        helper: "Help us prioritize which platforms to add next",
        options: ["Instagram", "TikTok", "Podcasts", "Blogs", "Other"]
      },
      primaryUseCase: {
        label: "What's your primary use case?",
        helper: "This helps us tailor the experience for you",
        options: ["Work research", "School study", "Content creation", "Personal learning", "Other"]
      },
      role: {
        label: "What's your role?",
        helper: "Understanding your role helps us customize features",
        options: ["Student", "Engineer", "Founder", "Researcher", "PM/Marketing", "Other"]
      },
      watchLaterSize: {
        label: "How big is your 'Watch Later' playlist?",
        helper: "This helps us understand your content consumption",
        options: ["< 20 videos", "20-100 videos", "100-300 videos", "300+ videos"]
      }
    },
    microcopy: {
      progressIndicator: "Step {current} of {total}",
      exitIntent: {
        title: "Wait! Don't lose your spot",
        message: "Join the Quellect waitlist and get early access when we launch.",
        cta: "Join Waitlist Now"
      },
      success: {
        title: "You're on the list! 🎉",
        message: "Thanks for joining the Quellect waitlist. We'll notify you as soon as we launch.",
        cta: "Share with friends"
      }
    }
  },
  efficiency_focus: {
    hero: {
      headline: "Save Hours of Video Rewatching",
      subheadline: "Quellect gives you instant access to any concept from your video library. Find what you need in seconds, not hours.",
      cta: "Join Waitlist"
    },
    form: {
      emailLabel: "Email Address",
      emailPlaceholder: "your.email@example.com",
      emailHelper: "We'll notify you when Quellect launches.",
      keepUpdatedLabel: "Keep me updated about Quellect",
      keepUpdatedHelper: "Get product updates and early access.",
      submitButton: "Join Waitlist",
      submitButtonLoading: "Joining..."
    },
    questions: {
      title: "Help us personalize your experience",
      subtitle: "A few quick questions to make Quellect perfect for you",
      whatWouldYouQuellect: {
        label: "What would you like to Quellect?",
        helper: "Tell us about the content you want to make searchable",
        placeholder: "e.g., Machine learning tutorials, cooking videos, business podcasts..."
      },
      nextPlatforms: {
        label: "Which platforms would you like us to support next?",
        helper: "Help us prioritize which platforms to add next",
        options: ["Instagram", "TikTok", "Podcasts", "Blogs", "Other"]
      },
      primaryUseCase: {
        label: "What's your primary use case?",
        helper: "This helps us tailor the experience for you",
        options: ["Work research", "School study", "Content creation", "Personal learning", "Other"]
      },
      role: {
        label: "What's your role?",
        helper: "Understanding your role helps us customize features",
        options: ["Student", "Engineer", "Founder", "Researcher", "PM/Marketing", "Other"]
      },
      watchLaterSize: {
        label: "How big is your 'Watch Later' playlist?",
        helper: "This helps us understand your content consumption",
        options: ["< 20 videos", "20-100 videos", "100-300 videos", "300+ videos"]
      }
    },
    microcopy: {
      progressIndicator: "Step {current} of {total}",
      exitIntent: {
        title: "Wait! Don't lose your spot",
        message: "Join the Quellect waitlist and get early access when we launch.",
        cta: "Join Waitlist Now"
      },
      success: {
        title: "You're on the list! 🎉",
        message: "Thanks for joining the Quellect waitlist. We'll notify you as soon as we launch.",
        cta: "Share with friends"
      }
    }
  }
};

export function getPersonaVariant(): PersonaVariant {
  // Simple seeding function - you can make this more sophisticated
  const variants: PersonaVariant[] = ['problem_solution', 'identity_based', 'future_state', 'efficiency_focus'];
  const randomIndex = Math.floor(Math.random() * variants.length);
  return variants[randomIndex];
}
